import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Button, Column, InputSelectField, Row, TextField, Typography } from '@tempmee/react';
import { SolidCheckCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { fetchPartnershipOrganizations } from '~/actions';
import { SectionEditDialog } from '~/components/shared/AccountArea';
import {
  bookedOutPatientsOptions,
  numberOfPermanentStaffOptions,
} from '~/components/registration/utils';
import {
  buildPartnerReferralPatch,
  buildProductivityPatch,
  toSectionDefaults,
  useUpdateLocation,
  useUpdateLocationProductivity,
  type IOperatingDetailsValues,
} from '~/domain/LocationSettings';
import { useAppSelector } from '~/hooks/useAppSelector';
import { toSelectOptions } from '~/utils/Enum';
import type { ISectionModalProps } from './types';

const STAFF_FIELDS = [
  { profession: 'RDH', label: 'Dental Hygienist on Staff (Optional)' },
  { profession: 'DA', label: 'Dental Assistants on Staff (Optional)' },
  { profession: 'DN', label: 'Doctors on Staff (Optional)' },
] as const;

const APPLY_ERROR_MESSAGE = 'That code could not be applied. Check it and try again.';

const UNAPPLIED_CODE_MESSAGE = 'Apply the code, or clear it, to save.';

/**
 * Permanent staff, booking-out window and the partner discount code. The code
 * applies straight away on Apply Code, as on Account Information, and is
 * read-only once applied; Save covers the rest.
 */
export const OperatingDetailsModal = ({
  officeId,
  office,
  onClose,
  onSaved,
}: ISectionModalProps) => {
  const dispatch = useDispatch();
  const organizations = useAppSelector((state) => state.user.partnershipOrganizations);
  const defaultValues = useMemo(() => toSectionDefaults(office, null).operatingDetails, [office]);
  const { control, handleSubmit, formState } = useForm<IOperatingDetailsValues>({ defaultValues });
  const values = useWatch({ control }) as IOperatingDetailsValues;
  const { dirtyFields } = formState;
  const updateProductivity = useUpdateLocationProductivity(officeId);
  const applyCode = useUpdateLocation(officeId);
  const [applyErrorMessage, setApplyErrorMessage] = useState<string>();

  // Once per open, as Account Information loads it on mount; keyed on the list
  // instead, an empty answer from the API would refetch in a loop.
  useEffect(() => {
    dispatch(fetchPartnershipOrganizations());
  }, [dispatch]);

  const appliedCode = office.partnerReferral?.partnershipCode;
  // Save covers staff and booking-out only; it is held while a typed code
  // waits for Apply Code, so leaving through Save never drops it unasked.
  const hasUnappliedCode =
    !appliedCode?.code && !!dirtyFields.partnershipCode && !!values.partnershipCode?.trim();
  const canSave =
    !!(dirtyFields.permanentStaff || dirtyFields.bookedOutPatients) && !hasUnappliedCode;

  const save = handleSubmit(async (submitted) => {
    await updateProductivity.mutateAsync(
      buildProductivityPatch(office, {
        permanentStaff: submitted.permanentStaff,
        bookedOutPatients: submitted.bookedOutPatients,
      }),
    );
    onSaved();
  });

  const apply = async () => {
    setApplyErrorMessage(undefined);
    try {
      await applyCode.mutateAsync(
        buildPartnerReferralPatch(values.partnershipOrganization, values.partnershipCode),
      );
    } catch {
      setApplyErrorMessage(APPLY_ERROR_MESSAGE);
    }
  };

  return (
    <SectionEditDialog
      title="Edit Operating Details"
      dataCy="edit-operating-details"
      onClose={onClose}
      onSave={save}
      canSave={canSave}
    >
      <Column gap={3}>
        <Typography variant="overline">Number of permanent staff</Typography>
        {STAFF_FIELDS.map(({ profession, label }) => (
          <Controller
            key={profession}
            name={`permanentStaff.${profession}`}
            control={control}
            render={({ field }) => (
              <InputSelectField
                label={label}
                options={toSelectOptions(numberOfPermanentStaffOptions)}
                value={field.value ?? undefined}
                onChange={field.onChange}
              />
            )}
          />
        ))}
      </Column>
      <Controller
        name="bookedOutPatients"
        control={control}
        render={({ field }) => (
          <InputSelectField
            label="How far are you booking out patients? (Optional)"
            options={toSelectOptions(bookedOutPatientsOptions)}
            value={field.value ?? undefined}
            onChange={field.onChange}
          />
        )}
      />
      {appliedCode?.code ? (
        <Column gap={2} data-cy="partner-code-applied">
          <TextField
            label="Dental Association/State Affiliation (Optional)"
            value={appliedCode.organization}
            disabled
            readOnly
          />
          <Row align="center" gap={2}>
            <Typography variant="body1">{appliedCode.code}</Typography>
            <SolidCheckCircleIcon aria-label="Applied" color={colors.success[500]} />
          </Row>
        </Column>
      ) : (
        <Column gap={3}>
          <Controller
            name="partnershipOrganization"
            control={control}
            render={({ field }) => (
              <InputSelectField
                label="Dental Association/State Affiliation (Optional)"
                options={toSelectOptions(organizations ?? [])}
                value={field.value || undefined}
                onChange={field.onChange}
              />
            )}
          />
          {values.partnershipOrganization && (
            <Row align="start" gap={2}>
              <Controller
                name="partnershipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Partner Discount Code"
                    helperText={
                      applyErrorMessage ?? (hasUnappliedCode ? UNAPPLIED_CODE_MESSAGE : undefined)
                    }
                    error={!!applyErrorMessage}
                    data-cy="partner-code"
                    {...field}
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={apply}
                loading={applyCode.isPending}
                disabled={!values.partnershipCode || applyCode.isPending}
                data-cy="apply-partner-code"
              >
                Apply Code
              </Button>
            </Row>
          )}
        </Column>
      )}
    </SectionEditDialog>
  );
};
